import { QueryClient, QueryClientProvider } from 'react-query'

import { ScreenClassProvider, SWAN_STYLE_KEY_MAP } from '@vp/swan'
import { IdentityProvider, useLogger, useStyles, useSwanStyleKeys, useUserContext } from '@vp/ubik-context'
import { LocalizationsProvider, type NestedStringObject } from '@vp/ubik-localizations-provider'

import { QuantityPageWrapper } from './quantity/addToCartWrapper/QuantityPageWrapper'
import { AuthorizationProvider } from './root/contexts/AuthorizationProvider'
import { PricingContextProvider } from './root/contexts/PricingContext'
import { TenantProvider } from './root/contexts/TenantContext'
import { AppContextProvider } from './root/contexts/AppContext'
import { ABTestContextProvider } from './root/contexts/ABTestContext'
import { parseQueryParameters } from './root/contexts/AppContext/Parameters/parseParameters'
import { stylesheet as quantityStyles } from './quantity/quantity.scss'
import { getCountryFromLocale } from '../utils/LocalizationHelpers'
import { getMerchantId } from '../utils/TenantDataHelpers'

export interface Props {
  authorizationHeader?: string
  localizations: NestedStringObject
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
})

export const Fragment = (props: Props) => {
  const logger = useLogger()
  const userContext = useUserContext()
  logger.log('Rendering fragment for a locale', userContext.locale)

  useStyles(quantityStyles)
  useSwanStyleKeys(
    Array.from(
      new Set([
        SWAN_STYLE_KEY_MAP.accordion,
        SWAN_STYLE_KEY_MAP.alertBox,
        SWAN_STYLE_KEY_MAP.button,
        SWAN_STYLE_KEY_MAP.carousel,
        SWAN_STYLE_KEY_MAP.core,
        SWAN_STYLE_KEY_MAP.grid,
        SWAN_STYLE_KEY_MAP.hidden,
        SWAN_STYLE_KEY_MAP.icon,
        SWAN_STYLE_KEY_MAP.modalDialog,
        SWAN_STYLE_KEY_MAP.progressiveImage,
        SWAN_STYLE_KEY_MAP.standardTile,
        SWAN_STYLE_KEY_MAP.table,
        SWAN_STYLE_KEY_MAP.toggleSwitch,
        SWAN_STYLE_KEY_MAP.visible,
        SWAN_STYLE_KEY_MAP.utility,
        SWAN_STYLE_KEY_MAP.spinner,
        SWAN_STYLE_KEY_MAP.popover,
        SWAN_STYLE_KEY_MAP.pricing,
      ]),
    ),
  )

  // const workId = userContext.queryStrings?.['workid']

  const createAuthConfig = (culture: string, isDevelopment = false) => ({
    culture,
    developmentMode: isDevelopment,
    options: {
      requireSession: true,
      customText: 'Sign in to see your account',
    },
  })

  const tenant = userContext.tenant ?? 'vistaprint'

  return (
    <ScreenClassProvider>
      <IdentityProvider auth={createAuthConfig(userContext.locale)}>
        <TenantProvider tenant={tenant}>
          <AuthorizationProvider authorizationHeader={props.authorizationHeader}>
            <PricingContextProvider tenant={getMerchantId(tenant)} market={getCountryFromLocale(userContext.locale)}>
              <QueryClientProvider client={queryClient}>
                <AppContextProvider queryParameters={parseQueryParameters()} locale={userContext.locale}>
                  <LocalizationsProvider localizations={props.localizations} logger={logger}>
                    <ABTestContextProvider>
                      <div className="post-design-configuration">
                        <div className="quantity-app">
                          <QuantityPageWrapper />
                        </div>
                      </div>
                    </ABTestContextProvider>
                  </LocalizationsProvider>
                </AppContextProvider>
              </QueryClientProvider>
            </PricingContextProvider>
          </AuthorizationProvider>
        </TenantProvider>
      </IdentityProvider>
    </ScreenClassProvider>
  )
}
